"use client";

import { useLayoutEffect, useRef, useState } from "react";

export function useSvgContentHeight(
  minHeight: number,
  deps: React.DependencyList = [],
) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [contentHeight, setContentHeight] = useState(minHeight);

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    let raf = 0;

    const measure = () => {
      const svgRect = svg.getBoundingClientRect();
      if (svgRect.height === 0) return;
      const vb = svg.viewBox.baseVal;
      const vbHeight = vb.height || minHeight;
      const scale = svgRect.height / vbHeight;

      let maxBottomVb = 0;

      try {
        const bbox = svg.getBBox();
        maxBottomVb = Math.max(maxBottomVb, bbox.y + bbox.height);
      } catch {}

      svg.querySelectorAll("foreignObject").forEach((fo) => {
        const child = fo.firstElementChild as HTMLElement | null;
        if (!child) return;
        const childRect = child.getBoundingClientRect();
        const bottomInVb = (childRect.bottom - svgRect.top) / scale;
        maxBottomVb = Math.max(maxBottomVb, bottomInVb);
      });

      const nextHeight = Math.max(minHeight, Math.ceil(maxBottomVb + 8));
      setContentHeight((prev) => (nextHeight !== prev ? nextHeight : prev));
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    schedule();

    const ro = new ResizeObserver(schedule);
    ro.observe(svg);

    const observed = new WeakSet<Element>();
    const observeContent = () => {
      svg.querySelectorAll("foreignObject > *").forEach((el) => {
        if (observed.has(el)) return;
        observed.add(el);
        ro.observe(el);
      });
    };
    observeContent();

    // The query above only sees what exists while the effect runs. Content that
    // mounts later — a node added by a data change, a label that only then
    // wraps — would never be observed, and the measured height would stay at
    // whatever the first pass found.
    const mo = new MutationObserver(() => {
      observeContent();
      schedule();
    });
    mo.observe(svg, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minHeight, ...deps]);

  return { svgRef, height: contentHeight };
}
